import React, { useMemo, useState } from 'react'
import {
  createColumnHelper,
  Table,
  Link,
  Text,
  Flex,
  Timeline,
  Label,
  Button,
} from '@workwhile/ui'
import { Work } from 'api/typings'
import moment from 'moment/moment'
import WorkerDetail from 'components/Shifts/Workers/WorkerDetail'
import { differenceInMinutes, isAfter } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone, getLongDuration } from 'lib/time'
import {
  ReviewShiftsProvider,
  useReviewShiftsContextValue,
} from './context/ReviewShiftsProvider'
import { useLeftEarlyShifts } from 'queries/review'
import { ReviewFilters } from './components/ReviewFilters/ReviewFilters'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { ShiftStatusRender } from 'pages/UnpaidShifts/components/UnpaidShiftsRenderers/ShiftStatusRender'
import { AmountRender } from 'pages/UserPayments/components/UserPaymentsRenderers/AmountRender'
import { TimelineTripModal } from './components/TimelineTripModal/TimelineTripModal'

const columnHelper = createColumnHelper<Work>()

function LeftEarlyQueueContent() {
  const {
    state: { data, pagination },
    actions: { refetch, setPagination },
  } = useReviewShiftsContextValue()

  const [selectedWork, setSelectedWork] = useState<Work | null>(null)

  const onActionSuccess = (action: string) => {
    if (action === 'mark_approved') {
      refetch()
    }
  }

  const removeShiftFromReviewList = (workId: number) => {
    // Refetch to update the list
    refetch()
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'Work ID',
        enableSorting: false,
      }),
      columnHelper.accessor('worker', {
        header: 'Worker',
        cell: ({ row }) => {
          const userId = row.original.worker?.id
          const userName = row.original.worker?.name
          const displayName = userName || ''
          return (
            <Text>
              {displayName ? `${displayName} (` : ''}
              <Link to={`/users/${userId}`} target="_blank">
                {userId}
              </Link>
              {displayName ? ')' : ''}
            </Text>
          )
        },
      }),
      columnHelper.accessor('shift', {
        header: 'Shift Info',
        enableSorting: false,
        cell: (info) => {
          const timezone =
            info.row.original.shift?.location?.address?.timezone ||
            getLocalTimezone()
          const cell = info.getValue()
          const shiftId = cell?.id
          const positionId = cell?.position?.id
          const positionName = cell?.position?.name
          const companyId = cell?.company?.id
          const companyName = cell?.company?.name

          return (
            <Flex flexDirection="column" gap={1}>
              <Text>
                <Text as="span" fontWeight="bold">
                  Shift:
                </Text>{' '}
                {shiftId ? (
                  <Link to={`/shifts/${shiftId}`} target="_blank">
                    #{shiftId}
                  </Link>
                ) : (
                  'N/A'
                )}
              </Text>
              {positionId && positionName ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Position:
                  </Text>{' '}
                  {positionName} (
                  <Link
                    to={`/position_editor?positionId=${positionId}`}
                    target="_blank"
                  >
                    #{positionId}
                  </Link>
                  )
                </Text>
              ) : null}
              {companyId && companyName ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Company:
                  </Text>{' '}
                  {companyName} (
                  <Link to={`/companies/${companyId}`} target="_blank">
                    #{companyId}
                  </Link>
                  )
                </Text>
              ) : null}
              {cell?.startsAt && cell?.endsAt ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Times:
                  </Text>{' '}
                  {moment
                    .tz(cell.startsAt, timezone)
                    .format('YYYY-MM-DD h:mm A')}{' '}
                  -{' '}
                  {moment.tz(cell.endsAt, timezone).format('YYYY-MM-DD h:mm A')}
                </Text>
              ) : null}
              {cell?.lunchLength !== undefined && cell?.lunchLength !== null ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Lunch (mins):
                  </Text>{' '}
                  {cell.lunchLength}
                </Text>
              ) : null}
              {cell?.supervisors && cell.supervisors.length > 0 ? (
                <>
                  <Text fontSize="sm">
                    <Text as="span" fontWeight="bold">
                      Supervisor(s):
                    </Text>
                  </Text>
                  {cell.supervisors.map((supervisor) => (
                    <Text key={supervisor.id} fontSize="sm">
                      {supervisor.name} (
                      <Link to={`/users/${supervisor.id}`} target="_blank">
                        {supervisor.id}
                      </Link>
                      ). Phone: {supervisor.phoneNumber || 'N/A'}
                    </Text>
                  ))}
                </>
              ) : null}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('earnings', {
        header: 'Pay',
        enableSorting: false,
        cell: (info) => (
          <AmountRender value={parseFloat(info.getValue() || '0')} />
        ),
      }),
      columnHelper.accessor('id', {
        header: 'Timeline',
        enableSorting: false,
        cell: (info) => {
          const work = info.row.original
          const timezone =
            work.shift?.location?.address?.timezone || getLocalTimezone()
          const shiftStart = work.shift?.startsAt
            ? new Date(work.shift.startsAt)
            : null
          const shiftEnd = work.shift?.endsAt
            ? new Date(work.shift.endsAt)
            : null
          const workerStart = work.startedAt ? new Date(work.startedAt) : null
          const workerEnd = work.completedAt ? new Date(work.completedAt) : null

          if (!shiftStart && !shiftEnd && !workerStart && !workerEnd) {
            return <Text>-</Text>
          }

          const timelineItems: Array<{
            label: string
            timestamp: Date
            badge?: { text: string; variant: 'success' | 'error' }
          }> = []

          // Determine overall badge color: green if worker starts earlier AND ends later
          // Shift Start
          if (shiftStart) {
            timelineItems.push({
              label: 'Shift Start',
              timestamp: shiftStart,
            })
          }

          // Worker Start
          if (workerStart) {
            let badge:
              | { text: string; variant: 'success' | 'error' }
              | undefined
            timelineItems.push({
              label: 'Worker Start',
              timestamp: workerStart,
              badge,
            })
          }

          // Worker End
          if (workerEnd) {
            let badge:
              | { text: string; variant: 'success' | 'error' }
              | undefined
            if (shiftEnd) {
              const diffMinutes = Math.abs(
                differenceInMinutes(shiftEnd, workerEnd)
              )
              // Only show badge if there's a meaningful difference (more than 1 minute)
              if (diffMinutes > 1) {
                const clockOutEarly = isAfter(shiftEnd, workerEnd)
                badge = {
                  text: `${getLongDuration(diffMinutes)}${clockOutEarly ? ' early' : ' late'}`,
                  variant: clockOutEarly ? 'error' : 'success',
                }
              }
            }
            timelineItems.push({
              label: 'Worker End',
              timestamp: workerEnd,
              badge,
            })
          }

          // Shift End
          if (shiftEnd) {
            timelineItems.push({
              label: 'Shift End',
              timestamp: shiftEnd,
            })
          }

          // Sort by timestamp
          timelineItems.sort(
            (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
          )

          return (
            <>
              <Text as="span" fontWeight="bold">
                Shift duration:
              </Text>
              <Text fontSize={2} mb={3}>
                {getLongDuration(
                  differenceInMinutes(
                    shiftEnd || new Date(),
                    shiftStart || new Date()
                  )
                )}
              </Text>
              <Timeline>
                {timelineItems.map((item, index) => {
                  const formattedTime = formatInTimeZone(
                    item.timestamp,
                    timezone,
                    'MMM d, yyyy, h:mm a z'
                  )

                  return (
                    <Timeline.Item
                      key={`${item.label}-${item.timestamp.getTime()}-${index}`}
                    >
                      <Flex alignItems="center" gap={1} flexWrap="wrap">
                        <Text color="lightText" fontSize={0}>
                          {formattedTime}
                        </Text>
                        {item.badge ? (
                          <Label
                            variant={item.badge.variant}
                            inline
                            size="small"
                          >
                            {item.badge.text}
                          </Label>
                        ) : null}
                      </Flex>
                      <Text fontSize={2}>{item.label}</Text>
                    </Timeline.Item>
                  )
                })}
              </Timeline>
              <Text fontSize={2} mb={2}>
                <Text as="span" fontWeight="bold">
                  Work Status:
                </Text>
                <ShiftStatusRender value={work.status || ''} />
              </Text>
              <Text fontWeight="bold">Early Clockout Reason:</Text>
              <Text fontSize={2}>{work.reasonEarlyClockout || 'N/A'}</Text>
            </>
          )
        },
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const record = info.row.original
          return (
            <>
              <Flex gap={2} mb={2}>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setSelectedWork(record)}
                >
                  View Timeline & Trip
                </Button>
              </Flex>
              <WorkerDetail
                shift={record?.shift}
                workId={record?.id}
                workerId={record?.worker?.id}
                onActionSuccess={onActionSuccess}
                removeShiftFromReviewList={() =>
                  record?.id && removeShiftFromReviewList(record.id)
                }
              />
            </>
          )
        },
      }),
    ],
    []
  )

  return (
    <>
      <Flex justifyContent="left" alignItems="center" mb={3}>
        <ReviewFilters />
      </Flex>
      <Table
        columns={columns}
        data={data.items}
        textVerticalAlign="top"
        enableSorting={true}
        defaultSort={[
          {
            id: 'completedAt',
            desc: false,
          },
        ]}
        alternateRowColor={true}
        serverPagination={{
          rowCount: data.totalRows,
          state: pagination,
          setState: setPagination,
        }}
      />
      {selectedWork ? (
        <TimelineTripModal
          open={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          work={selectedWork}
        />
      ) : null}
    </>
  )
}

export function LeftEarlyQueue() {
  return (
    <NuqsAdapter>
      <ReviewShiftsProvider
        useQueryHook={(options) => useLeftEarlyShifts(options)}
      >
        <LeftEarlyQueueContent />
      </ReviewShiftsProvider>
    </NuqsAdapter>
  )
}
